import React, { useState, useEffect } from "react";
import {
    Mic,
    MicOff,
    Square,
    Loader2,
    CheckCircle2,
    ArrowLeft,
    FileText,
    X,
    Pause,
    Play,
    Trash2,
    CloudUpload,
    AlertTriangle,
    RotateCcw,
    ChevronDown,
} from "lucide-react";
import { useMeetingRecording, SILENCE_WARNING_MS } from "../../../../context/MeetingContext";
import { transcriptStreamClient } from "../../../../utils/TranscriptStreamClient";
import { getMeetingSessionById } from "../../../../service/apiService";
import { MeetingSession } from "../../../../types";
import LiveTranscriptPanel from "./LiveTranscriptPanel";
import SendToBrainBankModal from "./SendToBrainBankModal";

interface MeetingRecorderProps {
    onComplete: (sessionId?: string) => void;
    onCancel: () => void;
}

// Seconds the silence dialog counts down before stopping and saving on
// its own. "Keep recording" cancels; the countdown pauses while the
// exit-confirm dialog is open (the user is clearly present), and never
// runs at all when system audio was requested but fell back to mic-only
// — the mic cannot hear the remote party, so silence proves nothing.
const SILENCE_STOP_COUNTDOWN_S = 15;

const MeetingRecorder: React.FC<MeetingRecorderProps> = ({ onComplete, onCancel }) => {
    const {
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        discardRecording,
        uploadedChunks,
        pendingUploadCount,
        recordingState,
        recordingDuration,
        currentSessionId,
        silenceWarning,
        dismissSilenceWarning,
        systemAudioUnavailable,
        micUnavailable,
    } = useMeetingRecording();

    const [includeSystemAudio, setIncludeSystemAudio] = useState(false);
    const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [isDiscarding, setIsDiscarding] = useState(false);
    const [completedSessionId, setCompletedSessionId] = useState<string | null>(null);
    const [liveTranscriptReady, setLiveTranscriptReady] = useState(false);
    // Phones cannot fit the transcript beside the recorder, so it collapses
    // behind a toggle instead of being hidden outright.
    const [showMobileTranscript, setShowMobileTranscript] = useState(false);
    const [silenceCountdown, setSilenceCountdown] = useState(SILENCE_STOP_COUNTDOWN_S);
    // WS-02 — offered straight from the completion modal, but only once the
    // transcript actually exists.
    const [brainBankSession, setBrainBankSession] = useState<MeetingSession | null>(null);
    const [loadingBrainBankSession, setLoadingBrainBankSession] = useState(false);

    // Format time from seconds
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Track recording state and stop function in refs for cleanup access
    // This is crucial to avoid running the cleanup when dependencies change (re-renders),
    // and only run it when the component actually unmounts.
    const recordingStateRef = React.useRef(recordingState);
    const stopRecordingRef = React.useRef(stopRecording);

    useEffect(() => {
        recordingStateRef.current = recordingState;
        stopRecordingRef.current = stopRecording;
    }, [recordingState, stopRecording]);

    // Unmount is NOT a decision to end the meeting. The recorder lives in
    // MeetingContext (mounted at the app root) and the transcript socket
    // is a module singleton, precisely so closing the meetings modal
    // leaves the recording running — headlessui routes the X, Esc AND a
    // backdrop click to the same close handler, and a stray Esc must not
    // end someone's meeting. Reopening restores this view; the sidebar
    // shows a live indicator meanwhile. Ending a recording stays an
    // explicit act: Stop, or the exit dialog on the back arrow.
    //
    // finalize_failed is the exception: nothing is being captured any
    // more and the audio is already uploaded, so an unmount retry is a
    // free last-ditch save.
    useEffect(() => {
        return () => {
            if (recordingStateRef.current === 'finalize_failed') {
                console.log('[MeetingRecorder] Unmounting after a failed finalize - retrying the save');
                // Pass 0 to let context calculate duration
                stopRecordingRef.current(0);
            }
        };
    }, []); // Empty dependency array ensures this ONLY runs on unmount

    // Errors (mic denied, session creation failure, …) are handled and
    // toasted by the context; a null session id just leaves us idle.
    const handleStart = async () => {
        await startRecording('New Recording', includeSystemAudio);
    };

    const handleStop = async () => {
        const sessionId = currentSessionId;
        const saved = await stopRecording(recordingDuration);
        // Only claim success when finalize actually succeeded — the
        // context already surfaced an error toast otherwise.
        if (sessionId && saved) {
            setCompletedSessionId(sessionId);
            // A gap-free live stream already produced the transcript of
            // record during the session (WS-01) — finalize completes
            // immediately and there is no batch job to wait for.
            setLiveTranscriptReady(
                transcriptStreamClient.getStatus() === 'completed' &&
                transcriptStreamClient.getSegments().some((s) => s.isStable)
            );
            setShowTranscriptionModal(true);
        }
    };

    // Ref so the silence countdown effect can stop without restarting
    // its interval on every render (handleStop is a fresh closure each
    // render).
    const handleStopRef = React.useRef(handleStop);
    useEffect(() => {
        handleStopRef.current = handleStop;
    });

    // Silence dialog countdown: ticks while the dialog shows, and at 0
    // stops and saves the recording. Cleaned up when the dialog is
    // dismissed, the recording leaves the recording state, or the exit
    // dialog takes over.
    const silenceDialogActive = silenceWarning
        && recordingState === 'recording'
        && !showExitConfirm;

    // The mic is the only source when system audio was requested but
    // never arrived, so silence means "you aren't talking" — not "the
    // meeting ended". Warn, but never stop a live call on our own.
    const silenceAutoStop = silenceDialogActive && !systemAudioUnavailable;

    useEffect(() => {
        if (!silenceAutoStop) return;
        setSilenceCountdown(SILENCE_STOP_COUNTDOWN_S);
        const intervalId = setInterval(() => {
            setSilenceCountdown(prev => Math.max(0, prev - 1));
        }, 1000);
        return () => clearInterval(intervalId);
    }, [silenceAutoStop]);

    useEffect(() => {
        if (silenceAutoStop && silenceCountdown === 0) {
            dismissSilenceWarning();
            void handleStopRef.current();
        }
    }, [silenceAutoStop, silenceCountdown, dismissSilenceWarning]);

    const handlePause = async () => {
        await pauseRecording();
    };

    const handleResume = async () => {
        await resumeRecording();
    };

    // The dialog needs the saved session (speaker mapping and gap flags drive
    // its warnings), which only exists server-side at this point.
    const handleSendToBrainBank = async () => {
        if (!completedSessionId) return;
        setLoadingBrainBankSession(true);
        try {
            const saved = await getMeetingSessionById(completedSessionId);
            if (saved) setBrainBankSession(saved);
        } catch (error) {
            console.error('Could not load the session to send:', error);
        } finally {
            setLoadingBrainBankSession(false);
        }
    };

    const handleModalDismiss = () => {
        setShowTranscriptionModal(false);
        if (completedSessionId) {
            onComplete(completedSessionId);
        }
    };

    const handleCancel = () => {
        // Leaving with an active recording (or an unsaved one after a
        // failed finalize) is a real decision — ask, don't silently save
        // (or worse, silently lose) the audio.
        if (recordingState === 'recording' || recordingState === 'paused' || recordingState === 'starting' || recordingState === 'finalize_failed') {
            setShowExitConfirm(true);
            return;
        }
        onCancel();
    };

    const handleExitSave = async () => {
        setShowExitConfirm(false);
        await handleStop();
        // handleStop shows the transcription modal on success; its
        // dismiss navigates via onComplete. On failure we stay here so
        // the user can retry stopping.
    };

    const handleExitDiscard = async () => {
        setIsDiscarding(true);
        try {
            await discardRecording();
            setShowExitConfirm(false);
            onCancel();
        } finally {
            setIsDiscarding(false);
        }
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={handleCancel}
                    aria-label="Go back"
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    disabled={recordingState === 'finalizing' || recordingState === 'stopping'}
                >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h2 className="text-lg font-semibold">New Recording</h2>
            </div>

            {/* Main content: recorder controls left; once a session is
                underway, the live transcript panel opens on the right
                (WS-01) with no extra user action. */}
            {/* Below sm this column scrolls: the recorder keeps its natural
                height (flex-none) instead of being squeezed by min-h-0, which
                made `justify-center` overflow its box and paint the controls
                on top of the transcript underneath. */}
            <div className="flex-1 flex flex-col sm:flex-row min-h-0 gap-4 sm:gap-6 overflow-y-auto sm:overflow-visible">
            <div className="w-full flex-none flex flex-col items-center justify-start sm:w-auto sm:flex-1 sm:min-h-0 sm:justify-center">
                {/* Recording visualization */}
                <div className={`
                    w-24 h-24 sm:w-32 sm:h-32 rounded-full flex items-center justify-center mb-4 sm:mb-8
                    ${recordingState === 'recording' ? 'bg-red-100 animate-pulse' :
                        recordingState === 'paused' ? 'bg-amber-100' : 'bg-gray-100'}
                `}>
                    {recordingState === 'recording' ? (
                        <Mic className="w-10 h-10 sm:w-12 sm:h-12 text-red-500" />
                    ) : recordingState === 'paused' ? (
                        <Pause className="w-10 h-10 sm:w-12 sm:h-12 text-amber-500" />
                    ) : recordingState === 'finalize_failed' ? (
                        <AlertTriangle className="w-10 h-10 sm:w-12 sm:h-12 text-amber-500" />
                    ) : recordingState === 'starting' || recordingState === 'stopping' || recordingState === 'finalizing' ? (
                        <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-violet-500 animate-spin" />
                    ) : (
                        <Mic className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
                    )}
                </div>

                {/* Timer */}
                <div className="text-3xl sm:text-4xl font-mono font-bold text-gray-900 mb-2 sm:mb-4">
                    {formatTime(recordingState === 'idle' ? 0 : recordingDuration)}
                </div>

                {/* Status text */}
                <div className="text-sm text-gray-500 mb-4 sm:mb-8 text-center px-2">
                    {recordingState === 'idle' && 'Ready to record'}
                    {recordingState === 'starting' && 'Starting recording...'}
                    {recordingState === 'recording' && 'Recording...'}
                    {recordingState === 'paused' && 'Paused — audio is not being captured'}
                    {recordingState === 'stopping' && 'Stopping recording...'}
                    {recordingState === 'finalizing' && 'Finalizing and processing...'}
                    {recordingState === 'finalize_failed' && 'Saving failed — your audio is safe. Retry saving.'}
                </div>

                {/* Chunk upload status */}
                {(recordingState === 'recording' || recordingState === 'paused') && (
                    <div className="flex flex-col items-center gap-1.5 mb-4 sm:mb-8">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Chunks uploaded: {uploadedChunks}
                        </div>
                        {micUnavailable && (
                            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-1 rounded-md max-w-md text-center">
                                <MicOff className="w-4 h-4 flex-shrink-0" />
                                Microphone stopped — your voice is no longer being recorded.
                                Stop and save, then start again.
                            </div>
                        )}
                        {systemAudioUnavailable && (
                            <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-md max-w-md text-center">
                                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                System audio isn&apos;t being captured — microphone only.
                                Other participants won&apos;t be in the transcript.
                            </div>
                        )}
                        {pendingUploadCount > 0 && (
                            <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-md">
                                <CloudUpload className="w-4 h-4" />
                                {pendingUploadCount} segment{pendingUploadCount > 1 ? 's' : ''} waiting to upload — retrying…
                            </div>
                        )}
                    </div>
                )}

                {/* Controls */}
                <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                    {recordingState === 'idle' && (
                        <div className="flex flex-col items-center gap-4">
                            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer hover:text-gray-900">
                                <input
                                    type="checkbox"
                                    checked={includeSystemAudio}
                                    onChange={(e) => setIncludeSystemAudio(e.target.checked)}
                                    className="w-4 h-4 text-violet-600 rounded border-gray-300 focus:ring-violet-500"
                                />
                                <span>Record system audio (Teams/Zoom)</span>
                            </label>
                            {includeSystemAudio && (
                                <div className="text-xs text-amber-600 bg-amber-50 px-3 py-1 rounded-md max-w-xs text-center">
                                    Note: You will be asked to select the meeting tab to capture audio.
                                </div>
                            )}
                            <button
                                onClick={handleStart}
                                className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors text-lg font-medium"
                            >
                                <Mic className="w-5 h-5" />
                                Start Recording
                            </button>
                        </div>
                    )}

                    {recordingState === 'recording' && (
                        <>
                            <button
                                onClick={handlePause}
                                className="flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-colors text-lg font-medium"
                            >
                                <Pause className="w-5 h-5" />
                                Pause
                            </button>
                            <button
                                onClick={handleStop}
                                className="flex items-center gap-2 px-6 py-3 bg-gray-800 text-white rounded-full hover:bg-gray-900 transition-colors text-lg font-medium"
                            >
                                <Square className="w-5 h-5" />
                                Stop Recording
                            </button>
                        </>
                    )}

                    {recordingState === 'paused' && (
                        <>
                            <button
                                onClick={handleResume}
                                className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors text-lg font-medium"
                            >
                                <Play className="w-5 h-5" />
                                Resume
                            </button>
                            <button
                                onClick={handleStop}
                                className="flex items-center gap-2 px-6 py-3 bg-gray-800 text-white rounded-full hover:bg-gray-900 transition-colors text-lg font-medium"
                            >
                                <Square className="w-5 h-5" />
                                Stop Recording
                            </button>
                        </>
                    )}

                    {recordingState === 'finalize_failed' && (
                        <>
                            <button
                                onClick={handleStop}
                                className="flex items-center gap-2 px-6 py-3 bg-violet-600 text-white rounded-full hover:bg-violet-700 transition-colors text-lg font-medium"
                            >
                                <RotateCcw className="w-5 h-5" />
                                Retry saving
                            </button>
                            <button
                                onClick={() => setShowExitConfirm(true)}
                                className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-red-600 rounded-full hover:bg-red-50 transition-colors text-lg font-medium"
                            >
                                <Trash2 className="w-5 h-5" />
                                Discard
                            </button>
                        </>
                    )}

                    {(recordingState === 'starting' || recordingState === 'stopping' || recordingState === 'finalizing') && (
                        <button
                            disabled
                            className="flex items-center gap-2 px-6 py-3 bg-gray-400 text-white rounded-full cursor-not-allowed text-lg font-medium"
                        >
                            <Loader2 className="w-5 h-5 animate-spin" />
                            {recordingState === 'starting' ? 'Starting...' :
                                recordingState === 'stopping' ? 'Stopping...' : 'Finalizing...'}
                        </button>
                    )}
                </div>
            </div>

            {/* Live transcript panel (WS-01) — appears automatically once
                recording starts. From sm it is the right-hand pane; below
                that it becomes a collapsible section under the recorder.
                The panel is mounted either way (only its visibility is
                toggled) so the transcript stream is never interrupted. */}
            {recordingState !== 'idle' && (
                <div className="flex flex-col min-h-0 w-full sm:w-[45%] sm:min-w-[320px] border-t sm:border-t-0 sm:border-l border-gray-200 pt-2 sm:pt-0 sm:pl-6">
                    <button
                        type="button"
                        onClick={() => setShowMobileTranscript((v) => !v)}
                        aria-expanded={showMobileTranscript}
                        className="sm:hidden flex items-center justify-between w-full px-1 py-2 text-sm font-medium text-gray-700"
                    >
                        {/* Reads as a control, not a title: the panel below
                            renders its own "Live Transcript" heading and
                            status pill, so repeating it here duplicated it. */}
                        <span className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            {showMobileTranscript ? 'Hide transcript' : 'Show live transcript'}
                        </span>
                        <ChevronDown
                            className={`w-4 h-4 transition-transform ${showMobileTranscript ? 'rotate-180' : ''}`}
                        />
                    </button>
                    <div
                        className={`relative flex-1 ${showMobileTranscript ? 'block min-h-[220px]' : 'hidden'} sm:block sm:min-h-0`}
                    >
                        <LiveTranscriptPanel isRecording={recordingState === 'recording'} />
                    </div>
                </div>
            )}
            </div>

            {/* Footer info */}
            <div className="text-center text-sm text-gray-400 pb-4">
                Audio is automatically saved every 30 seconds
            </div>

            {/* Silence confirmation — 30 s without audio. Stops and saves
                on its own unless the user cancels, except in mic-only
                fallback (systemAudioUnavailable), where it only warns. */}
            {silenceDialogActive && (
                <div className="fixed inset-0 z-50 flex items-center justify-center scrim">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 relative">
                        <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mb-4">
                                <MicOff className="w-8 h-8 text-amber-500" />
                            </div>

                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                Are you still there?
                            </h3>

                            <p className="text-sm text-gray-600 mb-6">
                                No audio has been detected for the last {Math.round(SILENCE_WARNING_MS / 60000)} minutes.
                                {silenceAutoStop ? (
                                    <> The recording will stop and save automatically
                                        in <span className="font-semibold text-gray-900">{silenceCountdown}s</span>.</>
                                ) : (
                                    <> Only your microphone is being recorded, so other
                                        participants are not heard. The recording keeps
                                        running until you stop it.</>
                                )}
                            </p>

                            <div className="flex items-center gap-3">
                                <button
                                    onClick={dismissSilenceWarning}
                                    className="px-5 py-2.5 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors"
                                >
                                    Keep recording
                                </button>
                                <button
                                    onClick={() => {
                                        dismissSilenceWarning();
                                        void handleStop();
                                    }}
                                    className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    <Square className="w-4 h-4" />
                                    Stop and save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Exit confirmation — leaving with an active recording */}
            {showExitConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center scrim">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 relative">
                        <button
                            onClick={() => setShowExitConfirm(false)}
                            disabled={isDiscarding}
                            aria-label="Keep recording"
                            className="absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-400" />
                        </button>

                        <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mb-4">
                                <Mic className="w-8 h-8 text-amber-500" />
                            </div>

                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                {recordingState === 'finalize_failed'
                                    ? 'Recording not saved yet'
                                    : 'Recording in progress'}
                            </h3>

                            <p className="text-sm text-gray-600 mb-6">
                                {recordingState === 'finalize_failed'
                                    ? 'Saving this recording failed, but your audio is safe. You can retry saving, or discard it. A discarded recording cannot be recovered.'
                                    : 'Do you want to save this recording before leaving, or discard it? A discarded recording cannot be recovered.'}
                            </p>

                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleExitDiscard}
                                    disabled={isDiscarding}
                                    className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    {isDiscarding ? 'Discarding…' : 'Discard'}
                                </button>
                                <button
                                    onClick={handleExitSave}
                                    disabled={isDiscarding}
                                    className="px-5 py-2.5 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50"
                                >
                                    {recordingState === 'finalize_failed' ? 'Retry saving' : 'Save recording'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Transcription Processing Modal */}
            {showTranscriptionModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center scrim">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 relative">
                        <button
                            onClick={handleModalDismiss}
                            className="absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-400" />
                        </button>

                        <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mb-4">
                                <FileText className="w-8 h-8 text-violet-600" />
                            </div>

                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                {liveTranscriptReady ? 'Transcript Ready' : 'Transcription in Progress'}
                            </h3>

                            <p className="text-sm text-gray-600 mb-6">
                                {liveTranscriptReady
                                    ? 'Your recording has been saved and the transcript from the live session is ready to view.'
                                    : 'Your recording has been saved and is now being transcribed. You will be notified once the transcription is complete and ready to use.'}
                            </p>

                            <div className="flex items-center justify-center gap-3 flex-wrap">
                                <button
                                    onClick={handleModalDismiss}
                                    className="px-6 py-2.5 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 transition-colors"
                                >
                                    Got it
                                </button>
                                {liveTranscriptReady && (
                                    <button
                                        onClick={handleSendToBrainBank}
                                        disabled={loadingBrainBankSession}
                                        className="px-6 py-2.5 border border-violet-600 text-violet-700 text-sm font-medium rounded-lg hover:bg-violet-50 transition-colors disabled:opacity-50 flex items-center gap-2"
                                    >
                                        {loadingBrainBankSession && (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        )}
                                        Send to BrainBank
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Send to BrainBank (WS-02). Rendered last on purpose: it opens
                from the completion modal above, which is also z-50, and the
                later sibling is the one that paints on top. */}
            {brainBankSession && (
                <SendToBrainBankModal
                    session={brainBankSession}
                    // Sent: the recording flow is done — leave for the meeting
                    // page rather than dropping back onto "Transcript Ready".
                    // Cancel/X still returns there (onClose only).
                    onExported={handleModalDismiss}
                    onClose={() => setBrainBankSession(null)}
                />
            )}
        </div>
    );
};

export default MeetingRecorder;
